"use client";

import React from "react";
import StickyBox from "react-sticky-box";
import { YouTubeEmbed, FacebookEmbed } from "react-social-media-embed";

import {
	Box,
	Card,
	Stack,
	SvgIcon,
	Typography,
	CardContent,
} from "@mui/material";

import { Markdown } from "@onair/minimal-ui/markdown";

import TextMaxLine from "@onair/ui/text-max-line";
import { formatCurrency } from "@onair/utils/format-number";
import { useAuthStore } from "@onair/store/auth";
import { isImgUrl, isFacebookVideo } from "@onair/utils/check-url";
import dayjs, {
	FORMAT_TIME,
	FORMAT_DATE_DAY_WITHOUT_YEAR,
} from "@onair/utils/dayjs";
import { SessionPriceType } from "@onair/repositories";

import { WizardSchemaType } from "../index";
import { Iconify } from "@onair/minimal-ui/iconify";
import { ImagePlaceholderSvg } from "@onair/ui/svg/image-placeholder";
import { EventTypeV2 } from "@onair/repositories";
import { EVENT_COLORS } from "@onair/ui/event/constants";
import EventSessionsPreview from "./event-sessions";
import { getLabelFromClientRoomProvider } from "@onair/constants";

interface PropTypes {
	data: WizardSchemaType;
}

const PreviewInfo: React.FC<PropTypes> = (props) => {
	const { data } = props;

	const isSingleEvent = data.type === EventTypeV2.SINGLE;

	const colors = EVENT_COLORS;

	const currentUser = useAuthStore((state) => state);

	const files = React.useMemo(() => {
		return [
			...(data.stepInfo.photos ?? []).map(({ file }) => file),
			...(data.stepInfo.videos ?? []).map(({ url }) => url),
		].filter(Boolean);
	}, [
		data.stepInfo._recalculatePhotosAndVideos,
		data.stepInfo.photos,
		data.stepInfo.videos,
	]);

	const sessionPriceType: SessionPriceType | null =
		data.type === EventTypeV2.SESSION ? data.stepPricing.priceType : null;

	const isFree = React.useMemo(() => {
		if (isSingleEvent) {
			return data.stepTimelineAndPricing.isFree;
		}
		if (sessionPriceType === SessionPriceType.ALL) {
			return data.stepPricing.isFree;
		}
		return false;
	}, [
		isSingleEvent,
		data.stepTimelineAndPricing.isFree,
		data.stepPricing.isFree,
		sessionPriceType,
	]);

	const farePrice = React.useMemo(() => {
		if (isSingleEvent) {
			return data.stepTimelineAndPricing.farePrice;
		}
		if (sessionPriceType === SessionPriceType.ALL) {
			return data.stepPricing.farePrice;
		}
		return 0;
	}, [
		isSingleEvent,
		data.stepTimelineAndPricing.farePrice,
		data.stepPricing.farePrice,
		sessionPriceType,
	]);

	const startDate = React.useMemo(() => {
		if (isSingleEvent) {
			return data.stepTimelineAndPricing.startDate || null;
		}

		const sessions = data.stepTimeline.sessions;
		if (sessions.length === 0) {
			return null;
		}

		return (
			sessions.reduce((prev, curr) => {
				if (!curr?.startDate) {
					return prev;
				}
				return prev?.startDate < curr.startDate ? prev : curr;
			}).startDate || null
		);
	}, [
		isSingleEvent,
		data.stepTimelineAndPricing.startDate,
		JSON.stringify(data.stepTimeline.sessions),
	]);

	const endDate = React.useMemo(() => {
		if (isSingleEvent) {
			return data.stepTimelineAndPricing.endDate || null;
		}

		const sessions = data.stepTimeline.sessions;
		if (sessions.length === 0) {
			return null;
		}

		return (
			sessions.reduce((prev, curr) => {
				if (!curr?.endDate) {
					return prev;
				}
				return prev?.endDate > curr.endDate ? prev : curr;
			}).endDate || null
		);
	}, [
		isSingleEvent,
		data.stepTimelineAndPricing.endDate,
		JSON.stringify(data.stepTimeline.sessions),
	]);

	const renderFile = React.useCallback(
		(
			file: string | File | null,
			width: string | number = "100%",
			height: string | number = 200,
		) => {
			if (!file) {
				return null;
			}

			if (typeof file === "string" && !isImgUrl(file)) {
				return (
					<Stack
						key={file}
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
						justifyContent="center"
						alignItems="center"
						overflow="hidden"
						width={width}
						height={height}
						className="rounded-lg"
					>
						{isFacebookVideo(file) ? (
							<FacebookEmbed url={file} width={width} height={height} />
						) : (
							<YouTubeEmbed url={file} width={width} height={height} />
						)}
					</Stack>
				);
			}

			return (
				<Box
					key={file?.name}
					width={width}
					height={height}
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
					overflow="hidden"
					className="rounded-lg"
					sx={{
						backgroundImage: `url(${file.type ? URL.createObjectURL(file as Blob) : file})`,
						backgroundSize: "cover",
						backgroundPosition: "center",
						backgroundRepeat: "no-repeat",
					}}
				/>
			);
		},
		[colors.bgColor, colors.borderColor],
	);

	const date = React.useMemo(() => {
		if (startDate && endDate && !dayjs(startDate).isSame(endDate, "d")) {
			return (
				<Stack direction="row" spacing={1} alignItems="center">
					<Box
						display="flex"
						flexDirection="column"
						bgcolor="rgba(0, 80, 255, 0.08)"
						borderRadius={1}
						overflow="hidden"
						width={48}
						height={45}
					>
						<Box py={0.2} px={1} bgcolor={colors.colorHighlight}>
							<Typography
								variant="body2"
								align="center"
								color="#fff"
								fontSize={9}
							>
								Thg {dayjs(startDate ?? new Date()).month() + 1}
							</Typography>
						</Box>
						<Box
							flexGrow={1}
							display="flex"
							alignItems="center"
							justifyContent="center"
						>
							<Typography
								variant="subtitle1"
								align="center"
								color={colors.color}
								fontSize={14}
							>
								{dayjs(startDate ?? new Date()).date()}
							</Typography>
						</Box>
					</Box>
					<Stack direction="row" spacing={0.5}>
						<Stack spacing={0}>
							<Typography
								variant="subtitle1"
								align="left"
								color={colors.color}
								fontSize={12}
								textTransform="capitalize"
							>
								{dayjs(startDate).format(FORMAT_DATE_DAY_WITHOUT_YEAR)}
							</Typography>
							<Typography
								flex={1}
								variant="subtitle1"
								align="left"
								color={colors.color}
								fontSize={12}
							>
								{dayjs(startDate).format(FORMAT_TIME)}
							</Typography>
						</Stack>
						<Typography
							variant="subtitle1"
							align="left"
							color={colors.color}
							fontSize={12}
						>
							-
						</Typography>
						<Stack spacing={0}>
							<Typography
								variant="subtitle1"
								align="left"
								color={colors.color}
								fontSize={12}
								textTransform="capitalize"
							>
								{dayjs(endDate).format(FORMAT_DATE_DAY_WITHOUT_YEAR)}
							</Typography>
							<Typography
								flex={1}
								variant="subtitle1"
								align="left"
								color={colors.color}
								fontSize={12}
							>
								{dayjs(endDate).format(FORMAT_TIME)}
							</Typography>
						</Stack>
					</Stack>
				</Stack>
			);
		}
		return (
			<Stack direction="row" spacing={1} alignItems="center">
				<Box
					display="flex"
					flexDirection="column"
					bgcolor="rgba(0, 80, 255, 0.08)"
					border="1px solid"
					borderColor={colors.borderColor}
					borderRadius={1}
					overflow="hidden"
					width={50}
					height={45}
				>
					<Box py={0.2} px={1} bgcolor={colors.colorHighlight}>
						<Typography
							variant="body2"
							align="center"
							color="#fff"
							fontSize={9}
						>
							Thg {dayjs(startDate ?? new Date()).month() + 1}
						</Typography>
					</Box>
					<Box
						flexGrow={1}
						display="flex"
						alignItems="center"
						justifyContent="center"
					>
						<Typography
							variant="subtitle1"
							align="center"
							color={colors.color}
							fontSize={14}
						>
							{dayjs(startDate ?? new Date()).date()}
						</Typography>
					</Box>
				</Box>
				{startDate ? (
					<Stack spacing={0}>
						<Typography
							variant="subtitle1"
							align="left"
							color={colors.color}
							fontSize={12}
							textTransform="capitalize"
						>
							{dayjs(startDate).format(FORMAT_DATE_DAY_WITHOUT_YEAR)}
						</Typography>
						<Typography
							flex={1}
							variant="subtitle1"
							align="left"
							color={colors.color}
							fontSize={12}
						>
							{dayjs(startDate).format(FORMAT_TIME)}{" "}
							{endDate && <>- {dayjs(endDate).format(FORMAT_TIME)}</>}
						</Typography>
					</Stack>
				) : (
					<Stack spacing={6 / 8} flex={1}>
						<Box
							className="w-1/2 h-5 rounded"
							bgcolor={colors.bgColor}
							border={`1px solid ${colors.borderColor}`}
						/>
						<Box
							className="w-1/2 h-4 rounded-3xl"
							bgcolor={colors.bgColor}
							border={`1px solid ${colors.borderColor}`}
						/>
					</Stack>
				)}
			</Stack>
		);
	}, [startDate, endDate]);

	const room = React.useMemo(() => {
		return (
			<Stack direction="row" spacing={1} alignItems="center">
				<Box
					display="flex"
					flexDirection="column"
					bgcolor={colors.bgColor}
					border="1px solid"
					borderColor={colors.borderColor}
					borderRadius={1}
					overflow="hidden"
					width={50}
					height={45}
					alignItems="center"
					justifyContent="center"
				>
					{data.stepTimelineAndPricing.roomProvider && (
						<Iconify
							icon="solar:videocamera-record-bold"
							color={colors.colorHighlight}
							width={24}
						/>
					)}
				</Box>
				{data.stepTimelineAndPricing.roomProvider ? (
					<Stack spacing={0}>
						<Typography
							variant="subtitle1"
							align="left"
							color={colors.color}
							fontSize={12}
							textTransform="capitalize"
						>
							Nền tảng
						</Typography>
						<Typography
							flex={1}
							variant="subtitle1"
							align="left"
							color={colors.color}
							fontSize={12}
							fontWeight="normal"
						>
							{getLabelFromClientRoomProvider(
								data.stepTimelineAndPricing.roomProvider,
							)}
						</Typography>
					</Stack>
				) : (
					<Stack spacing={6 / 8} flex={1}>
						<Box
							className="w-1/2 h-5 rounded"
							bgcolor={colors.bgColor}
							border={`1px solid ${colors.borderColor}`}
						/>
						<Box
							className="w-1/2 h-4 rounded-3xl"
							bgcolor={colors.bgColor}
							border={`1px solid ${colors.borderColor}`}
						/>
					</Stack>
				)}
			</Stack>
		);
	}, [data.stepTimelineAndPricing.roomProvider]);

	const host = React.useMemo(() => {
		return (
			<Stack direction="row" spacing={1} alignItems="center">
				<Box
					display="flex"
					flexDirection="column"
					bgcolor={colors.bgColor}
					border="1px solid"
					borderColor={colors.borderColor}
					borderRadius={1}
					overflow="hidden"
					width={50}
					height={45}
					alignItems="center"
					justifyContent="center"
				>
					<Iconify
						icon="iconoir:microphone-solid"
						color={colors.colorHighlight}
					/>
				</Box>
				<Stack spacing={0}>
					<Typography
						variant="subtitle1"
						align="left"
						color={colors.color}
						fontSize={12}
						textTransform="capitalize"
					>
						Host
					</Typography>
					<Stack direction="row" spacing={0.5} alignItems="center">
						<Iconify
							icon="carbon:user-avatar-filled"
							color={colors.colorHighlight}
							width={12}
						/>
						<Typography
							flex={1}
							variant="subtitle1"
							align="left"
							color={colors.color}
							fontSize={12}
							fontWeight="normal"
						>
							{currentUser?.fullName}
						</Typography>
					</Stack>
				</Stack>
			</Stack>
		);
	}, [data.stepTimelineAndPricing.roomProvider]);

	const banner = React.useMemo(() => {
		return data.stepInfo.banner ? (
			<Box
				className="w-full rounded-xl"
				bgcolor={colors.bgColor}
				border={`1px solid ${colors.borderColor}`}
				sx={{
					backgroundImage: `url(${typeof data.stepInfo.banner === "string" ? data.stepInfo.banner : URL.createObjectURL(data.stepInfo.banner as Blob)})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
					aspectRatio: "21/9",
				}}
			/>
		) : (
			<Box
				sx={{ aspectRatio: "21/9" }}
				display="flex"
				alignItems="center"
				justifyContent="center"
				className="w-full rounded-xl"
				bgcolor={colors.bgColor}
				border={`1px solid ${colors.borderColor}`}
			>
				<ImagePlaceholderSvg />
			</Box>
		);
	}, [colors.bgColor, colors.borderColor, data.stepInfo.banner]);

	const filesList = React.useMemo(() => {
		return files.length > 0 ? (
			<>
				{renderFile(files[0])}
				<Stack direction="row" spacing={1} justifyContent="center">
					{files.slice(0, 3)?.map((el) => {
						return renderFile(el, 40, 40);
					})}
					{files.length > 3 && (
						<Stack
							alignItems="center"
							justifyContent="center"
							className="size-10 rounded-lg"
							bgcolor={colors.bgColor}
							border={`1px solid ${colors.borderColor}`}
						>
							<Typography
								align="center"
								variant="body1"
								color={colors.color}
								fontSize={14}
							>
								+{files.length - 3}
							</Typography>
						</Stack>
					)}
				</Stack>
			</>
		) : (
			<>
				<Box
					className="w-full h-[200px] rounded-xl"
					display="flex"
					alignItems="center"
					justifyContent="center"
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
				>
					<ImagePlaceholderSvg />
				</Box>
				<Stack direction="row" spacing={1} justifyContent="center">
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						className="size-10 rounded-lg"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					>
						<ImagePlaceholderSvg size="sm" />
					</Box>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						className="size-10 rounded-lg"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					>
						<ImagePlaceholderSvg size="sm" />
					</Box>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						className="size-10 rounded-lg"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					>
						<ImagePlaceholderSvg size="sm" />
					</Box>
					<Box
						display="flex"
						alignItems="center"
						justifyContent="center"
						className="size-10 rounded-lg"
						bgcolor={colors.bgColor}
						border={`1px solid ${colors.borderColor}`}
					>
						<ImagePlaceholderSvg size="sm" />
					</Box>
				</Stack>
			</>
		);
	}, [colors.bgColor, colors.borderColor, colors.color, files, renderFile]);

	const ticket = React.useMemo(() => {
		return (
			<Stack
				spacing={1}
				height={125}
				maxHeight={125}
				justifyContent="flex-end"
				className="w-[160px] rounded-xl"
				bgcolor={colors.bgLightColor}
				border={`1px solid ${colors.borderColor}`}
				p={1}
			>
				<Stack
					direction="row"
					spacing={1}
					alignItems="center"
					justifyContent="space-between"
				>
					{farePrice || isFree ? (
						<>
							<Typography
								variant="body2"
								align="left"
								color={colors.color}
								fontSize={10}
							>
								Giá vé
							</Typography>
							{isFree ? (
								<Typography
									flex={1}
									variant="subtitle1"
									align="right"
									color={colors.color}
									fontSize={12}
								>
									Miễn phí
								</Typography>
							) : (
								<Typography
									flex={1}
									variant="subtitle1"
									align="right"
									color={colors.color}
									fontSize={12}
								>
									{formatCurrency(farePrice)}
								</Typography>
							)}
						</>
					) : (
						<>
							<Box
								className="w-1/3 h-5 rounded"
								bgcolor={colors.bgLabelColor}
							/>
							<Box
								className="w-1/3 h-6 rounded"
								bgcolor={colors.bgLabelColor}
							/>
						</>
					)}
				</Stack>
				<Box position="relative" height={15}>
					<Stack
						position="absolute"
						left="-15px"
						right="-15px"
						direction="row"
						spacing={1}
						alignItems="center"
					>
						<Box className="size-4 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-2 rounded-full bg-white" />
						<Box className="size-4 rounded-full bg-white" />
					</Stack>
				</Box>
				<Stack direction="row" spacing={1} alignItems="center">
					<Stack
						alignItems="center"
						justifyContent="center"
						className="w-7 h-7 rounded"
						bgcolor="#fff"
						border={`1px solid ${colors.borderColor}`}
					>
						<SvgIcon sx={{ fontSize: 16, color: colors.color }}>
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M2.14651 1.74156C2.0448 1.70587 1.93307 1.71203 1.83591 1.75871C1.73875 1.80539 1.6641 1.88875 1.62841 1.99046C1.59271 2.09217 1.59887 2.2039 1.64555 2.30106C1.69223 2.39822 1.77559 2.47287 1.8773 2.50856L2.01868 2.55786C2.38051 2.68515 2.6183 2.76911 2.79326 2.85523C2.95738 2.93594 3.02943 3.00148 3.07709 3.07081C3.12584 3.14231 3.16376 3.2409 3.18543 3.43752C3.20709 3.64336 3.20818 3.91148 3.20818 4.31231V5.72173C3.20818 7.31531 3.2423 7.84073 3.71193 8.33581C4.18101 8.8309 4.93609 8.8309 6.44734 8.8309H9.31926C10.1648 8.8309 10.5878 8.8309 10.8863 8.58715C11.1853 8.3434 11.2709 7.92956 11.4415 7.1019L11.7123 5.78836C11.9003 4.84586 11.994 4.37515 11.7535 4.06261C11.513 3.75006 10.6918 3.75006 9.77913 3.75006H4.01634C4.01404 3.61655 4.00627 3.48319 3.99305 3.35031C3.9638 3.08111 3.90097 2.83573 3.74822 2.61256C3.59438 2.38723 3.38963 2.24315 3.15076 2.12561C2.92759 2.01619 2.64376 1.91652 2.31009 1.79898L2.14651 1.74156ZM7.54151 4.96881C7.64925 4.96881 7.75258 5.01162 7.82877 5.0878C7.90496 5.16399 7.94776 5.26732 7.94776 5.37506V6.05215H8.62484C8.73259 6.05215 8.83592 6.09495 8.91211 6.17114C8.98829 6.24732 9.03109 6.35065 9.03109 6.4584C9.03109 6.56614 8.98829 6.66947 8.91211 6.74566C8.83592 6.82185 8.73259 6.86465 8.62484 6.86465H7.94776V7.54173C7.94776 7.64948 7.90496 7.75281 7.82877 7.82899C7.75258 7.90518 7.64925 7.94798 7.54151 7.94798C7.43377 7.94798 7.33043 7.90518 7.25425 7.82899C7.17806 7.75281 7.13526 7.64948 7.13526 7.54173V6.86465H6.45818C6.35043 6.86465 6.2471 6.82185 6.17091 6.74566C6.09473 6.66947 6.05193 6.56614 6.05193 6.4584C6.05193 6.35065 6.09473 6.24732 6.17091 6.17114C6.2471 6.09495 6.35043 6.05215 6.45818 6.05215H7.13526V5.37506C7.13526 5.26732 7.17806 5.16399 7.25425 5.0878C7.33043 5.01162 7.43377 4.96881 7.54151 4.96881Z"
									fill={colors.color}
								/>
								<path
									d="M4.56234 10.2501C4.77783 10.2501 4.98449 10.3357 5.13687 10.488C5.28924 10.6404 5.37484 10.8471 5.37484 11.0626C5.37484 11.2781 5.28924 11.4847 5.13687 11.6371C4.98449 11.7895 4.77783 11.8751 4.56234 11.8751C4.34685 11.8751 4.14019 11.7895 3.98782 11.6371C3.83545 11.4847 3.74984 11.2781 3.74984 11.0626C3.74984 10.8471 3.83545 10.6404 3.98782 10.488C4.14019 10.3357 4.34685 10.2501 4.56234 10.2501ZM9.43734 10.2501C9.65283 10.2501 9.85949 10.3357 10.0119 10.488C10.1642 10.6404 10.2498 10.8471 10.2498 11.0626C10.2498 11.2781 10.1642 11.4847 10.0119 11.6371C9.85949 11.7895 9.65283 11.8751 9.43734 11.8751C9.22185 11.8751 9.01519 11.7895 8.86282 11.6371C8.71045 11.4847 8.62484 11.2781 8.62484 11.0626C8.62484 10.8471 8.71045 10.6404 8.86282 10.488C9.01519 10.3357 9.22185 10.2501 9.43734 10.2501Z"
									fill={colors.color}
								/>
							</svg>
						</SvgIcon>
					</Stack>
					<Stack
						alignItems="center"
						justifyContent="center"
						className="flex-1 h-7 rounded"
						bgcolor={colors.colorHighlight}
						border={`1px solid ${colors.borderColor}`}
					>
						<Typography
							variant="body2"
							align="center"
							color="#fff"
							fontSize={12}
						>
							Tham gia
						</Typography>
					</Stack>
				</Stack>
			</Stack>
		);
	}, [farePrice, isFree]);

	const content = React.useMemo(() => {
		return data.stepInfo.content ? (
			<>
				<Typography variant="subtitle2" color={colors.color}>
					Giới thiệu
				</Typography>
				<TextMaxLine line={10}>
					<Markdown sx={{ "& p": { fontSize: 12, color: colors.color } }}>
						{data.stepInfo.content}
					</Markdown>
				</TextMaxLine>
			</>
		) : (
			<>
				<Box
					className="w-full h-5 rounded"
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
				/>
				<Box
					className="w-full h-[250px] rounded-lg"
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
				/>
			</>
		);
	}, [colors.bgColor, colors.borderColor, colors.color, data.stepInfo.content]);

	const name = React.useMemo(() => {
		return data.stepInfo.name ? (
			<Typography
				sx={{ wordBreak: "break-all" }}
				variant="h5"
				color={colors.color}
			>
				{data.stepInfo.name}
			</Typography>
		) : (
			<Box
				className="w-full h-8 rounded"
				bgcolor={colors.bgColor}
				border={`1px solid ${colors.borderColor}`}
			/>
		);
	}, [colors.bgColor, colors.borderColor, colors.color, data.stepInfo.name]);

	const hashtags = React.useMemo(() => {
		return data.stepInfo.hashtags?.length > 0 ? (
			<Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
				{data.stepInfo.hashtags?.map((el) => {
					return (
						<Typography
							component="span"
							key={el}
							variant="body2"
							align="center"
							color={colors.color}
							fontSize={10}
						>
							#{el}
						</Typography>
					);
				})}
			</Stack>
		) : (
			<Stack direction="row" spacing={1} alignItems="center">
				<Box
					className="w-full h-4 rounded"
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
				/>
			</Stack>
		);
	}, [
		colors.bgColor,
		colors.borderColor,
		colors.color,
		data.stepInfo.hashtags,
	]);

	const categories = React.useMemo(() => {
		return data.stepInfo.categories?.length > 0 ? (
			<Stack direction="row" spacing={1} alignItems="center">
				{data.stepInfo.categories?.map((el) => {
					return (
						<Stack
							key={el.value}
							alignItems="center"
							justifyContent="center"
							px={1}
							className="h-6 rounded"
							bgcolor={colors.bgColor}
							border={`1px solid ${colors.borderColor}`}
						>
							<Typography
								variant="body2"
								align="center"
								color={colors.color}
								fontSize={11}
							>
								{el.label}
							</Typography>
						</Stack>
					);
				})}
			</Stack>
		) : (
			<Stack direction="row" spacing={1} alignItems="center">
				<Box
					className="w-1/4 h-4 rounded"
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
				/>
				<Box
					className="w-1/4 h-4 rounded"
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
				/>
				<Box
					className="w-1/4 h-4 rounded"
					bgcolor={colors.bgColor}
					border={`1px solid ${colors.borderColor}`}
				/>
			</Stack>
		);
	}, [
		colors.bgColor,
		colors.borderColor,
		colors.color,
		data.stepInfo.categories,
	]);

	return (
		<Box
			flex={1}
			mx="auto"
			maxWidth="50%"
			display={{ xs: "none", md: "block" }}
		>
			<StickyBox className="stickyBox" offsetTop={100} offsetBottom={20}>
				<Card>
					<Stack bgcolor={colors.bgLightColor} p={2} spacing={2}>
						<Stack direction="row" spacing={1}>
							<Box className="size-3 rounded-full bg-white" />
							<Box className="size-3 rounded-full bg-white" />
							<Box className="size-3 rounded-full bg-white" />
						</Stack>
						<Stack direction="row" spacing={1} alignItems="center">
							<Stack direction="row" spacing={1} alignItems="center" flex={1}>
								<Box className="w-[20%] h-5 rounded bg-white" />
								<Box className="w-[60%] h-5 rounded bg-white" />
							</Stack>
							<Stack direction="row" spacing={1} alignItems="center">
								<Box className="size-4 rounded-full bg-white" />
								<Box className="size-4 rounded-full bg-white" />
								<Box className="size-4 rounded-full bg-white" />
								<Box className="size-6 rounded-full bg-white" />
							</Stack>
						</Stack>
					</Stack>
					<CardContent>
						<Stack spacing={2}>
							{banner}

							<Stack direction="row" spacing={2}>
								<Stack spacing={1} flex={1}>
									{name}
									{date}
									{isSingleEvent && room}
									{host}
									{categories}
									{hashtags}
									{!isSingleEvent && <EventSessionsPreview data={data} />}
								</Stack>
								{ticket}
							</Stack>
							<Stack direction="row" spacing={3}>
								<Stack spacing={1} flex={1}>
									{filesList}
								</Stack>
								<Stack spacing={1} flex={1}>
									{content}
								</Stack>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			</StickyBox>
		</Box>
	);
};

export default React.memo(PreviewInfo);
